import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Header, Icon, Input, Label, Grid, Dropdown, ListItem, List, ListContent, Button, Message, Checkbox } from 'semantic-ui-react'
import NumberInput from 'semantic-ui-react-numberinput'
import { ActionButton, LoadingWrapper } from "../common";
import { VacationLimitOverrides } from '../TeamCalendar/VacationLimitOverrides';
import { useNavigate } from 'react-router-dom';
import { canEditAll, isICCSupport, getCSRFToken, fetchWithAuth } from '../auth';
import { ASSOCIATE_ROLES, ASSOCIATE_MANAGER_ROLE, MANAGER_ROLE } from "../../constants/common"
import { timezones } from '../common/timezones';
import './Teams.css'

const vacationLimitToStr = (val) => val != null ? String(val) : ''

export const TeamsDetails = () => {

    const [team, setTeam] = useState({ members: [], name: '' });
    const [initialTeam, setinitialTeam] = useState(null);
    const [availableMembers, setAvailableMembers] = useState([]);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [teamTypes, setTeamTypes] = useState([]);
    const [succeeded, setSucceeded] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [loaded, setLoaded] = useState(false)
    const [membersLoaded, setMembersLoaded] = useState(false)
    const [teamTypesLoaded, setTeamTypesLoaded] = useState(false)
    const [vacationLimit, setVacationLimit] = useState('')
    const [vacationLimitOverrides, setVacationLimitOverrides] = useState([])
    const [overridesLoading, setOverridesLoading] = useState(false)
    const [markets, setMarkets] = useState([])
    const [marketsLoaded, setMarketsLoaded] = useState(false)
    const { id } = useParams();
    const [isLandscape, setIsLandscape] = useState(window.matchMedia('(orientation:landscape)').matches)


    const updateLandscape = () => {
        setIsLandscape(window.matchMedia('(orientation:landscape)').matches)
    }
    window.addEventListener('resize', updateLandscape)

    const navigate = useNavigate();


    useEffect(() => {
        fetchTeamDetails();
        fetchAvailableMembers();
        fetchTeamTypes();
        fetchMarkets();
        if (isICCSupport()) fetchVacationLimitOverrides();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchVacationLimitOverrides = async () => {
        setOverridesLoading(true);
        try {
            const resp = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}vacation-limit-overrides/?team=${id}`,
                { headers: { 'X-CSRFToken': getCSRFToken() }, credentials: 'include' }
            );
            if (resp.ok) {
                setVacationLimitOverrides(await resp.json());
            }
        } finally {
            setOverridesLoading(false);
        }
    };

    const fetchTeamDetails = async () => {
        setLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `teams/${id}/`, {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        let respJson = await response.json();
        const membersCopy = structuredClone(respJson['members'])
        delete respJson['members']
        let data = {
            "members": [],
            "assocManager": null,
            "managers": [],
            ...respJson
        }
        membersCopy.forEach(member => {
            const member_role = member.role.toLowerCase()
            if (ASSOCIATE_ROLES.includes(member_role)) {
                data.members.push(member)
            } else if (member_role === ASSOCIATE_MANAGER_ROLE) {
                data.assocManager = member
            } else if (member_role === MANAGER_ROLE) {
                data.managers.push(member)
            }
        })
        setTeam(data)
        setinitialTeam(data)
        setVacationLimit(vacationLimitToStr(data.vacationLimit ?? data.teamType?.vacationLimit))
        setLoaded(true)
    };

    const fetchAvailableMembers = async () => {
        setMembersLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'availablemembers/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        const data = await response.json();
        setAvailableMembers(data);
        setMembersLoaded(true)
        //handleMemberOptions()

    };

    const fetchTeamTypes = async () => {
        setTeamTypesLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'team-types/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        const data = await response.json();
        setTeamTypes(data);
        setTeamTypesLoaded(true)
    };

    const fetchMarkets = async () => {
        setMarketsLoaded(false)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'teams/markets/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include'
        });
        const data = await response.json();
        setMarkets(data);
        setMarketsLoaded(true)
    };


    function handleChange(key, value) {
        const teamCopy = structuredClone(team)
        teamCopy[key] = value
        setTeam(teamCopy)
    }

    const updateSelectedMembers = (event, data) => {
        setSelectedMembers(data.value)
    }

    const handleTeamNameChange = (e) => {
        setTeam({ ...team, name: e.target.value });
    };
    const handleRemoveMember = (memberId) => {
        handleChange('members', team.members.filter(member => member.userId !== memberId))
    };

    const handleRemoveAssocManager = () => {
        handleChange('assocManager', null)
    };

    const handleRemoveManager = (managerId) => {
        handleChange('managers', team.managers.filter(manager => manager.userId !== managerId))
    };

    const handleCancel = () => {
        setTeam({ ...initialTeam });
        setVacationLimit(vacationLimitToStr(initialTeam?.vacationLimit ?? initialTeam?.teamType?.vacationLimit))
    };

    const getSelectedMemberIds = () => {
        return selectedMembers.map(member => {
            // A team can only have one associate manager so remove the old one if adding a new one
            if (member.role.toLowerCase() === ASSOCIATE_MANAGER_ROLE && team.assocManager) {
                team.assocManager = null
            }
            return member.userId
        })
    }



    const handleUpdateTeam = async () => {
        // Validate vacation limit before doing anything
        if (isICCSupport() && team.teamType && vacationLimit !== '') {
            const parsed = parseInt(vacationLimit, 10)
            if (isNaN(parsed) || parsed < 1) {
                setErrorMessage('Daily PTO Limit must be a positive integer (≥ 1), or leave blank for no limit.')
                return
            }
        }

        setLoaded(false)
        setErrorMessage('')
        setSucceeded(false)

        // Update team name and members
        let selectedMemberIds = getSelectedMemberIds()
        let managerIds = team.managers.map(mgr => mgr.userId)
        let assocManagerIds = team.assocManager ? team.assocManager.userId : []
        let memberIds = team.members.map(member => member.userId).concat(selectedMemberIds, managerIds, assocManagerIds)
        const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `/teams/${id}/`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken()
            },
            body: JSON.stringify({
                name: team.name,
                members: memberIds,
                minimumMembersToBeReady: team.minimumMembersToBeReady,
                assets: team.assets,
                timezone: team.timezone,
                team_type_id: team.teamType?.id || null,
                isActive: team.isActive ?? true,
                market: team.market ?? null,
                ...(isICCSupport() && { vacation_limit: vacationLimit === '' ? null : parseInt(vacationLimit, 10) })
            }),
        });
        window.scrollTo({ top: 0, behavior: 'smooth' })
        if (response.ok) {
            setErrorMessage('')
            setSucceeded(true)
            const updatedTeam = await response.json();
            const membersCopy = structuredClone(updatedTeam['members'])
            delete updatedTeam['members']
            let data = {
                "members": [],
                "assocManager": null,
                "managers": [],
                ...updatedTeam
            }
            membersCopy.forEach(member => {
                const member_role = member.role.toLowerCase()
                if (ASSOCIATE_ROLES.includes(member_role)) {
                    data.members.push(member)
                } else if (member_role === ASSOCIATE_MANAGER_ROLE) {
                    data.assocManager = member
                } else if (member_role === MANAGER_ROLE) {
                    data.managers.push(member)
                }
            })
            setTeam(data);
            setSelectedMembers([])
            setinitialTeam(data)
            setVacationLimit(vacationLimitToStr(data.vacationLimit))
        } else {
            const respJson = await response.json()
            const errors = []
            for (const field in respJson) {
                const error = field + ": " + respJson[field]
                errors.push(error)
            }
            setSucceeded(false)
            setErrorMessage('Could not save.  ' + errors.join(", "))
        }
        setLoaded(true)
    };


    return (
        <LoadingWrapper text="Loading team" loading={!loaded}>
            <div align="center">
                {succeeded && <Message style={{ width: "75%" }} positive>Saved successfully!</Message>}
                {errorMessage !== '' && <Message style={{ width: "75%" }} negative>{errorMessage}</Message>}
                <div>
                    <div>
                        <Header size='medium'>Team: {team.name}</Header>
                    </div>
                    <div>
                        <Grid centered>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='center'></Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Team Name</Label></Grid.Column>
                                <Grid.Column width={8}>
                                    <Input
                                        type="text"
                                        id="teamName"
                                        value={team.name}
                                        onChange={handleTeamNameChange}
                                        readOnly={!canEditAll()}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Market</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Dropdown
                                        disabled={!canEditAll()}
                                        loading={!marketsLoaded}
                                        selection
                                        search
                                        clearable
                                        placeholder="Select market"
                                        options={markets.map((m) => ({
                                            key: m.key,
                                            value: m.key,
                                            text: m.value
                                        }))}
                                        value={team.market ?? null}
                                        onChange={(ev, data) => handleChange('market', data.value || null)}
                                        style={{ width: isLandscape ? "50%" : "100%" }}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Minimum members to be "Ready"</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <NumberInput
                                        disabled={!canEditAll()}
                                        value={team.minimumMembersToBeReady ? team.minimumMembersToBeReady : 0}
                                        onChange={(value) => { handleChange('minimumMembersToBeReady', parseInt(value)) }}
                                        maxValue={(team.members && team.managers) ? (team.members.length + team.managers.length) : 0}
                                        allowEmptyValue />
                                </Grid.Column>
                            </Grid.Row>

                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Assets</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>{team.assets && team.assets.length === 0 ? (
                                    <p>No assets for this team.</p>
                                ) : (
                                    <List relaxed className="list">
                                        {team.assets && team.assets.map((asset, i) => {
                                            const assetName = asset['iccName']
                                            const assetId = asset['id']
                                            return (
                                                <div key={i} className='list-item'>
                                                    <ListItem key={assetId}>
                                                        <ListContent style={{ fontWeight: "bold" }} as='a' onClick={() => navigate("/assets/" + assetId + '/view')}>{assetName}</ListContent>
                                                    </ListItem>
                                                </div>)
                                        })}
                                    </List>
                                )
                                }
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Team Type</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Dropdown
                                        disabled={!canEditAll()}
                                        loading={!teamTypesLoaded}
                                        selection
                                        search
                                        clearable
                                        placeholder="Select team type"
                                        options={teamTypes.map((type) => ({
                                            key: type.id,
                                            value: type.id,
                                            text: type.name
                                        }))}
                                        value={team.teamType?.id || null}
                                        onChange={(ev, data) => {
                                            const newType = teamTypes.find(t => t.id === data.value) || null
                                            handleChange('teamType', newType)
                                            setVacationLimit(vacationLimitToStr(newType?.vacationLimit))
                                        }}
                                        style={{ width: isLandscape ? "50%" : "100%" }}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            {team.teamType && (
                                <Grid.Row>
                                    <Grid.Column width={4} textAlign='right'>
                                        <Label size="large" active pointing="right">Daily PTO Limit</Label>
                                    </Grid.Column>
                                    <Grid.Column width={8}>
                                        {isICCSupport() ? (
                                            <Input
                                                type="number"
                                                min={1}
                                                placeholder="No limit"
                                                value={vacationLimit}
                                                onChange={(e) => setVacationLimit(e.target.value)}
                                                style={{ width: isLandscape ? "50%" : "100%" }}
                                            />
                                        ) : (
                                            <span style={{ lineHeight: "2.5rem" }}>
                                                {(() => {
                                                    const effective = team.vacationLimit ?? teamTypes.find(t => t.id === team.teamType?.id)?.vacationLimit
                                                    return effective != null ? effective : "No limit"
                                                })()}
                                            </span>
                                        )}
                                    </Grid.Column>
                                </Grid.Row>
                            )}
                            {isICCSupport() && team.teamType && (
                                <Grid.Row>
                                    <Grid.Column width={4} textAlign='right'>
                                        <Label size="large" active pointing="right">Date Overrides</Label>
                                    </Grid.Column>
                                    <Grid.Column width={8}>
                                        <VacationLimitOverrides
                                            lockedTeam={{ id: team.id, name: team.name }}
                                            overrides={vacationLimitOverrides}
                                            loading={overridesLoading}
                                            onOverrideAdded={newOverride =>
                                                setVacationLimitOverrides(prev => [...prev, newOverride])
                                            }
                                            onOverrideDeleted={deletedId =>
                                                setVacationLimitOverrides(prev => prev.filter(o => o.id !== deletedId))
                                            }
                                        />
                                    </Grid.Column>
                                </Grid.Row>
                            )}
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Timezone</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Dropdown
                                        selection
                                        search
                                        options={timezones.map((timezone) => {
                                            return {
                                                key: timezone.abbr,
                                                value: timezone.abbr,
                                                text: `${timezone.value} (${timezone.abbr})`
                                            }
                                        })}
                                        value={team.timezone}
                                        onChange={(ev, data) => handleChange('timezone', data.value)}
                                        style={{ width: isLandscape ? "50%" : "100%" }}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Team Status</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>
                                    <Checkbox
                                        data-testid="team-active-toggle"
                                        toggle
                                        label={team.isActive === false ? 'Inactive' : 'Active'}
                                        checked={team.isActive !== false}
                                        onChange={(e, data) => handleChange('isActive', data.checked)}
                                        disabled={!canEditAll()}
                                    />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Managers</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>{team.managers && team.managers.length === 0 ? (
                                    <p>No managers for this team.</p>
                                ) : (
                                    <List relaxed className="list">
                                        {team.managers && team.managers.map((manager, i) => {
                                            const managerName = manager['name']
                                            const managerId = manager['userId']
                                            return (
                                                <div key={i} className='list-item'>
                                                    <ListItem key={managerId}>
                                                        <ListContent style={{ fontWeight: "bold" }} as='a' onClick={() => navigate("/members/" + managerId + '/view')}>{managerName}</ListContent>
                                                    </ListItem>
                                                    {canEditAll() && <Button onClick={() => handleRemoveManager(managerId)} style={{ width: "fit-content", height: "fit-content", padding: "0.5rem", margin: "0" }}>
                                                        <Icon style={{ margin: "0" }} color='red' link name='close' />
                                                    </Button>}
                                                </div>)
                                        })}
                                    </List>
                                )
                                }

                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Associate Managers</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>{!team.assocManager ? (
                                    <p>No associate manager for this team.</p>
                                ) : (
                                    <List relaxed className="list">
                                        <div className='list-item'>
                                            <ListItem key={team.assocManager.userId}>
                                                <ListContent style={{ fontWeight: "bold" }} as='a' onClick={() => navigate("/members/" + team.assocManager.userId + '/view')}>{team.assocManager.name}</ListContent>
                                            </ListItem>
                                            {canEditAll() && <Button onClick={() => handleRemoveAssocManager()} style={{ width: "fit-content", height: "fit-content", padding: "0.5rem", margin: "0" }}>
                                                <Icon style={{ margin: "0" }} color='red' link name='close' />
                                            </Button>}
                                        </div>
                                    </List>
                                )
                                }
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column width={4} textAlign='right'>
                                    <Label size="large" active pointing="right">Team Members</Label>
                                </Grid.Column>
                                <Grid.Column width={8}>{team.members.length === 0 ? (
                                    <p>No members in this team.</p>
                                ) : (
                                    <List relaxed className="list">
                                        {team.members.map((member, i) => {
                                            const memberName = member['name']
                                            const memberId = member['userId']
                                            return (
                                                <div key={i} className='list-item'>
                                                    <ListItem key={memberId}>
                                                        <ListContent style={{ fontWeight: "bold" }} as='a' onClick={() => navigate("/members/" + memberId + '/view')}>{memberName}</ListContent>
                                                    </ListItem>
                                                    {canEditAll() && <Button onClick={() => handleRemoveMember(memberId)} style={{ width: "fit-content", height: "fit-content", padding: "0.5rem", margin: "0" }}>
                                                        <Icon style={{ margin: "0" }} color='red' link name='close' />
                                                    </Button>}
                                                </div>)
                                        })}
                                    </List>
                                )
                                }
                                </Grid.Column>
                            </Grid.Row>
                            {canEditAll() && (
                                <>
                                    <Grid.Row>
                                        <Grid.Column width={4} textAlign='right'>
                                            <Label size="large" active pointing="right">Add Members:</Label>
                                        </Grid.Column>
                                        <Grid.Column width={8}>
                                            <Dropdown loading={!membersLoaded} style={{ width: isLandscape ? "50%" : "100%" }} placeholder="Members" value={selectedMembers} onChange={updateSelectedMembers} fluid multiple search selection options={
                                                availableMembers.filter(member => !team.members.some(m => m.userId === member.userId) && team.managers && !team.managers.some(m => m.userId === member.userId))
                                                    .map(member => {
                                                        return {
                                                            key: member.userId,
                                                            value: member,
                                                            text: member.name
                                                        }
                                                    })
                                            } />
                                        </Grid.Column>
                                    </Grid.Row>
                                    <Grid.Row centered>
                                        <ActionButton submitAction={handleUpdateTeam}
                                            resetHide={false}
                                            resetText={'Cancel'}
                                            showConfirmation={true}
                                            resetAction={handleCancel}
                                        />
                                    </Grid.Row>
                                </>)}
                        </Grid>
                    </div>
                </div>
            </div>
        </LoadingWrapper>
    )

};
